/**
 * Shared-filter persistence for card-view widgets. HPS-140920
 *
 * On a cold (unauthenticated) session the portal performs an anonymous-login / login step that can
 * reload the page during widget init. A shared (filter) URL keeps its filter only in the URL
 * fragment and in an in-memory config value, so if that reload lands on a URL whose filter fragment
 * has been dropped, the portlet renders unfiltered (HPS-140920).
 *
 * These helpers persist the incoming filter in session storage so it survives the reload, and on
 * the reloaded page restore both the in-memory value (config.filterURL) and the URL fragment so the
 * filter panel/checkboxes and the shareable URL repopulate exactly as a normal shared-URL load.
 *
 * The restore only fires when the current page load is a browser RELOAD (the cold-session auth
 * reload) - not a genuine user navigation to the plain page - so a later plain visit is never
 * filtered with a stale value. The stash is consumed as soon as it is read.
 *
 * Usage (in a widget init):
 *   const shareFilter = require('civicsUtils/share_filter_persistence');
 *   shareFilter.captureOrRestore(options, App.params.get() || {}, 'instrumentslist');
 *
 * Widgets that key off more than one fragment (e.g. My Dashboard handles both 'assignments' and
 * 'projects') may pass an array; the matched key is also written to options.filterType.
 */
define(function (require) {
	const STASH_PREFIX = 'SHARE_FILTER_';

	function toKeyList(paramKeys) {
		return Array.isArray(paramKeys) ? paramKeys : [paramKeys];
	}

	function stashKey(paramKey) {
		return STASH_PREFIX + paramKey.toUpperCase();
	}

	// True when the current page load was a browser reload (e.g. the cold-session anonymous-login
	// window.location.reload()), as opposed to a fresh navigation/typed URL/link click. Used so the
	// stash is only re-applied to the reload that lost it, never to a deliberate plain visit.
	function wasReload() {
		try {
			const navEntries = performance.getEntriesByType && performance.getEntriesByType('navigation');
			if (navEntries && navEntries.length) {
				return navEntries[0].type === 'reload';
			}
			// Legacy fallback (performance.navigation.TYPE_RELOAD === 1)
			return !!(performance.navigation && performance.navigation.type === 1);
		} catch (e) {
			return false;
		}
	}

	return {
		/**
		 * Capture the shared filter from the URL params into options.filterURL and persist it; or,
		 * when the URL no longer carries it and the page was reloaded (an auth reload dropped the
		 * fragment), restore it from the stash and reinstate the URL fragment.
		 *
		 * @param {Object} options    widget options (mutated: filterURL, and filterType for multi-key)
		 * @param {Object} urlParams  the result of App.params.get()
		 * @param {string|string[]} paramKeys  fragment key(s), e.g. 'instrumentslist' or ['assignments','projects']
		 */
		captureOrRestore: function (options, urlParams, paramKeys) {
			const keys = toKeyList(paramKeys);
			const params = urlParams || {};
			let i;

			// 1) Filter present in the URL -> capture into options and persist it so it can survive
			//    a cold-session reload that happens later during init.
			for (i = 0; i < keys.length; i++) {
				if (params[keys[i]]) {
					options.filterURL = params[keys[i]];
					if (keys.length > 1) {
						options.filterType = keys[i];
					}
					App.sessionStorage.set('CIVICS', stashKey(keys[i]), options.filterURL);
					return;
				}
			}

			// 2) Filter absent. Only restore if this load was a reload (the cold-session auth reload
			//    that dropped the fragment) - not a genuine navigation to the plain page. The stash
			//    is consumed on read either way, so a deliberate plain visit drops any stale value.
			const isReload = wasReload();
			for (i = 0; i < keys.length; i++) {
				const sk = stashKey(keys[i]);
				const stashed = App.sessionStorage.get('CIVICS', sk);
				if (stashed) {
					if (isReload) {
						options.filterURL = stashed;
						if (keys.length > 1) {
							options.filterType = keys[i];
						}
						if (!window.location.hash) {
							window.location.hash = keys[i] + '/' + stashed;
						}
					}
					App.sessionStorage.remove('CIVICS', sk);
					return;
				}
			}
		}
	};
});
